import { ChevronDown20Regular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';

interface ExplorerSectionProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  badge?: ReactNode;
  children: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  surface?: 'default' | 'subtle';
  className?: string;
  bodyClassName?: string;
}

const surfaceStyles = {
  default: 'rounded-[1.75rem] border border-slate-200 bg-white shadow-sm',
  subtle: 'rounded-[1.5rem] border border-slate-200 bg-slate-50 shadow-sm',
} as const;

const bodyStyles = {
  default: 'border-t border-slate-200/80 px-5 py-5',
  subtle: 'border-t border-slate-200/80 px-4 py-4',
} as const;

export function ExplorerSection({
  title,
  subtitle,
  actions,
  badge,
  children,
  isOpen,
  onToggle,
  surface = 'default',
  className,
  bodyClassName,
}: ExplorerSectionProps) {
  return (
    <section className={`${surfaceStyles[surface]} ${className ?? ''}`.trim()}>
      <div className="flex items-start gap-4 px-5 py-4">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 items-start gap-3 text-left">
          <span
            aria-hidden="true"
            className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 transition ${isOpen ? 'rotate-0' : '-rotate-90'}`}
          >
            <ChevronDown20Regular />
          </span>
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold text-slate-900">{title}</span>
              {badge}
            </span>
            {subtitle && <span className="mt-1 block text-sm text-slate-500">{subtitle}</span>}
          </span>
        </button>

        {actions && <div className="shrink-0">{actions}</div>}
      </div>

      {isOpen && <div className={`${bodyStyles[surface]} ${bodyClassName ?? ''}`.trim()}>{children}</div>}
    </section>
  );
}

export default ExplorerSection;